/**
 * Authentication guard: ingest ?token=, or redirect to Auth Lambda login.
 *
 * Plan: docs/dashboard_impl_plan.md — D1.16–D1.19
 */

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import {
  handleTokenFromUrl,
  isAuthenticated,
  login,
  getCurrentUser,
  logout,
} from './session';

interface User {
  email: string;
  name: string;
}

interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthGuardProps {
  children: ReactNode;
}

/**
 * Hook to access the current user and auth state.
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthGuard');
  }
  return context;
}

/**
 * Authentication guard that ensures the user is logged in.
 */
export function AuthGuard({ children }: AuthGuardProps): JSX.Element {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    function initAuth(): void {
      const fromUrl = handleTokenFromUrl();
      if (fromUrl !== null) {
        setUser(fromUrl);
        setIsLoading(false);
        return;
      }

      if (isAuthenticated()) {
        const userData = getCurrentUser();
        if (userData !== null) {
          setUser(userData);
          setIsLoading(false);
          return;
        }
      }

      try {
        login();
      } catch (error) {
        // eslint-disable-next-line no-console -- Error logging for misconfiguration
        console.error('Auth login redirect failed:', error);
        setIsLoading(false);
      }
    }

    initAuth();
  }, []);

  if (isLoading) {
    return (
      <div className="auth-loading">
        <p>Authenticating...</p>
      </div>
    );
  }

  const contextValue: AuthContextValue = {
    user,
    isLoading,
    logout,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}
